import React, { Fragment, useState, useRef, useEffect } from 'react';
import { Redirect, useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { NUMBER_OF_SKELETON_TABLE_ROWS, NUMBER_OF_SKELETON_TABLE_FILTERS_ROWS } from '../../styles/constants';
import { useFetchCategoryData } from '../../hooks/useFetchCategoryData';
import { API_STATES } from '../../api';
import { QUICK_FILTERS } from '../../components/QuickFilters/constants';
import { useStyles } from './styles';
import withBreadcrumbs from '../App/HOCs/withBreadcrumbs';
import QuickFilters from '../../components/QuickFilters';
import FiltersPanel from '../../components/FiltersPanel';
import ProductsTable from '../../components/ProductsTable';
import Skeleton from '@material-ui/lab/Skeleton';
import ProductsTableSkeleton from './../../components/Skeleton/ProductsTableSkeleton';
import FiltersPanelSkeleton from './../../components/Skeleton/FiltersPanelSkeleton';
import QuickFiltersSkeleton from '../../components/Skeleton/QuickFiltersSkeleton/QuickFiltersSkeleton';
import QuickFiltersSkeletonWdSupported from './../../components/Skeleton/QuickFiltersSkeleton/QuickFiltersSkeletonWdSupported';
import { COLORS } from '../../styles/constants';
import Grid from '@material-ui/core/Grid';
import {
	updateFilterAppliedValue,
	updateFilterAppliedLogic,
	updateSimilarParts
} from '../../redux/slices/filtersSlice';
import { resetMessages, resetHistory } from '../../redux/slices/chatBotSlice';
import { CIDS } from '../../components/ProductsTable/constants';
import { adobPageView } from '../../utils/analytics/adobe/adob';
import api from '../../api';
import pushToGTM, { openCategoryTagGA4, topologyOpenedByGA4 } from '../../utils/analytics/gtm';
import CATEGORY_HIERARCHY from '../../utils/constants/category';
import {
	covertOldParameterToNew,
	checkIfLinkNeedConversion,
	removeUnsupportedValuesFromFilters
} from '../../utils/linkBackwardCompatibility';
import { localLog, localView, recordEvent } from '../../utils/analytics/local';

const ProductsPage = ({ category }) => {
	const classes = useStyles();
	const [filtersPanelOpen, setFiltersPanelOpen] = useState(true);
	const { status } = useFetchCategoryData(category.apiParam);
	const applicationOriented = useSelector(state => state.categoryData.applicationOriented);
	const location = useLocation();
	const history = useHistory();
	const dispatch = useDispatch();
	const filters = useSelector(state => state.categoryData.filters);
	const filtersOptions = useSelector(state => state.filters.filtersOptions);
	const calledOnce = useRef(false);
	const renderAfterCalled = useRef(false);
	const { t } = useTranslation(['category']);

	if (calledOnce.current) {
		renderAfterCalled.current = true;
	}

	const getCategoryByPathParam = pathParam => {
		let category = CATEGORY_HIERARCHY;
		category = category.categories.find(cat => cat.pathParam === pathParam);
		return t('category:' + category.id + '.abbreviation');
	};

	const handleFiltersOpen = open => {
		setFiltersPanelOpen(open);
	};

	const parseSimilarParts = currentFilters =>
		currentFilters[CIDS.SIMILAR_PARTS].replace('(', '').replace(')', '').split(';');

	useEffect(() => {
		let query = encodeURIComponent(window.location.search.substring(1));
		api.shouldLogIn(category.id).then(data => {
			if (data) {
				history.push('/');
				let url = window.location.href + category.pathParam + '/products';
				if (query) {
					url += '?' + query;
				}
				window.location = data + '?referer=' + url;
			}
		});
		let decodedURL = decodeURI(location.search);
		const currentFilters = queryString.parse(decodedURL);
		const pathname = location.pathname;
		const pathparts = pathname.split('/');
		const categoryId = pathparts[1];
		const filtersCID = [];
		filters.forEach(item => filtersCID.push(item.cid));
		const trimParams = cid => {
			if (
				!(
					cid === CIDS.SIMILAR_PARTS_FOR_WPN ||
					cid === CIDS.SIMILAR_PARTS_FOR_OPN ||
					cid === CIDS.SIMILAR_PARTS ||
					cid === 'utm_source' ||
					cid === 'utm_medium' ||
					cid === 'utm_region' ||
					cid === 'utm_content' ||
					cid === 'utm_campaign'
				)
			) {
				const updatedFilters = queryString.parse(decodedURL);
				delete updatedFilters[cid];
				decodedURL = queryString.stringify(updatedFilters, {
					encode: false
				});
				history.push({
					search: encodeURI(decodedURL)
				});
			}
		};

		const updateFiltersURL = updatedFilters => {
			decodedURL = queryString.stringify(updatedFilters, {
				encode: false
			});
			history.push({
				search: encodeURI(decodedURL)
			});
			return updatedFilters;
		};

		if (status === API_STATES.SUCCESS) {
			if (
				(currentFilters[CIDS.SIMILAR_PARTS_FOR_WPN] || currentFilters[CIDS.SIMILAR_PARTS_FOR_OPN]) &&
				currentFilters[CIDS.SIMILAR_PARTS]
			) {
				const similarParts = 'Similar Parts';
				pushToGTM(topologyOpenedByGA4(similarParts));

				let wpn = currentFilters[CIDS.SIMILAR_PARTS_FOR_WPN] ? currentFilters[CIDS.SIMILAR_PARTS_FOR_WPN] : '';
				let similarPartsList = parseSimilarParts(currentFilters);
				const obj = {
					wpn: wpn,
					similar_parts: similarPartsList
				};
				recordEvent('SIMILAR_PARTS', JSON.stringify(obj));
				dispatch(
					updateSimilarParts({
						wpn: wpn,
						opn: currentFilters[CIDS.SIMILAR_PARTS_FOR_OPN] ? currentFilters[CIDS.SIMILAR_PARTS_FOR_OPN] : '',
						similarParts: similarPartsList,
						filterAutoset: currentFilters[CIDS.FILTER_AUTOSET]
					})
				);
			} else {
				const urlLink = 'URL link';
				pushToGTM(topologyOpenedByGA4(urlLink));
			}

			if (!calledOnce.current) {
				calledOnce.current = true;
				localView(pathparts[1]);
				localLog(pathparts[1]);
				adobPageView(getCategoryByPathParam(categoryId));
				pushToGTM(openCategoryTagGA4(categoryId));
			}

			//Condition For link replacement
			if (Object.keys(currentFilters).length > 0 && checkIfLinkNeedConversion(category.id, currentFilters)) {
				const updatedFilters = covertOldParameterToNew(category.id, currentFilters);
				updateFiltersURL(updatedFilters);
			}

			//String filters values check, value is removed if it is not supported by PRT
			let updatedCurrentFilters = updateFiltersURL(
				removeUnsupportedValuesFromFilters(currentFilters, filtersOptions, filters)
			);

			Object.entries(updatedCurrentFilters).forEach(([cid, valueString]) => {
				if (!filtersCID.includes(cid) || valueString === null || valueString.trim() === '') {
					trimParams(cid);
					return;
				}

				if (valueString.includes('!')) {
					dispatch(updateFilterAppliedLogic({ cid, value: 'NOT' }));
				}

				const value = parseValue(cid, valueString);
				if (!Number.isNaN(value[0])) {
					dispatch(updateFilterAppliedValue({ cid, value, categoryId }));
				}
			});
		}
		dispatch(resetMessages());
		dispatch(resetHistory());
		// eslint-disable-next-line
	}, [status]);

	const parseValue = (cid, valueString) => {
		const multiSelect = filters.find(filter => filter.cid === cid).multiSelect;
		const singleSelect = filters.find(filter => filter.cid === cid).conditional;

		if (Array.isArray(valueString)) {
			return valueString.map(item => (!isNaN(item) ? parseFloat(item) : item));
		}

		if (valueString === 'true' || valueString === 'false') {
			return JSON.parse(valueString);
		}

		if (valueString === '(true)' || valueString === '(false)') {
			valueString = valueString.replace(/[()]/g, '');
			return JSON.parse(valueString);
		}

		if (valueString[0] === '!') {
			valueString = valueString.slice(1);
		}

		if (valueString[0] === '(' && valueString[valueString.length - 1] === ')') {
			if (!multiSelect && singleSelect) {
				return valueString.slice(1, -1).replace(/_/g, ' ');
			} else {
				return valueString
					.slice(1, -1)
					.split(';')
					.map(item => (!isNaN(item) ? parseFloat(item) : item.trim().replace(/_/g, ' ').replace(/~/g, ',')));
			}
		}

		return !isNaN(valueString) ? parseFloat(valueString) : valueString;
	};

	/* TODO: implement skeleton */
	if (status === API_STATES.PENDING || status === API_STATES.LOADING)
		return (
			<div>
				{category.apiParam !== '/powerSupply' && (
					<QuickFiltersSkeleton className={classes.quickFiltersContainer} numberOfRows={4} />
				)}
				{category.apiParam === '/powerSupply' && (
					<QuickFiltersSkeletonWdSupported className={classes.quickFiltersContainer} numberOfRows={5} />
				)}

				<br />
				<div className={classes.tableSection}>
					<div className={classes.filtersPanel}>
						<FiltersPanelSkeleton numberOfRows={NUMBER_OF_SKELETON_TABLE_FILTERS_ROWS} />
					</div>

					<div
						className={clsx(classes.productsTable, {
							[classes.productsTableShift]: filtersPanelOpen
						})}>
						<ProductsTableSkeleton numberOfRows={NUMBER_OF_SKELETON_TABLE_ROWS} />
					</div>
				</div>
			</div>
		);
	if (status === API_STATES.ERROR) return <Redirect push to="/error" />;
	return (
		<Fragment>
			{QUICK_FILTERS[category.id] && QUICK_FILTERS[category.id].filterGroups.length > 0 && (
				<section className={classes.quickFiltersContainer}>
					<QuickFilters category={category} wdSupported={category.wdSupported} />
				</section>
			)}
			<div className={classes.tableSection}>
				<div className={classes.filtersPanel}>
					<FiltersPanel categoryId={category.id} filtersOpen={filtersPanelOpen} handleFiltersOpen={handleFiltersOpen} />
				</div>

				<div
					className={clsx(classes.productsTable, {
						[classes.productsTableShift]: filtersPanelOpen
					})}>
					{status === API_STATES.PENDING || status === API_STATES.LOADING ? (
						<Skeleton />
					) : (
						<Grid
							style={{
								padding: applicationOriented && '4px',
								boxShadow: applicationOriented && `inset 0px 0px 4px 0.4px ${COLORS.PRIMARY}`,
								borderRadius: applicationOriented && '4px',
								backgroundColor: COLORS.WHITE
							}}>
							<ProductsTable
								categoryId={category.id}
								filtersOpen={filtersPanelOpen}
								handleFiltersOpen={handleFiltersOpen}
							/>
						</Grid>
					)}
				</div>
			</div>
		</Fragment>
	);
};

ProductsPage.propTypes = {
	category: PropTypes.object.isRequired
};

export default withBreadcrumbs(ProductsPage);
